// errors.mjs - HTTP-specific error classes

// Base HTTP Error class
export class HttpError extends Error {
    constructor(message, statusCode = 500, code = null, details = null) {
        super(message)
        this.name = this.constructor.name
        this.statusCode = statusCode
        this.code = code
        this.details = details
        this.timestamp = new Date().toISOString()

        // Capture stack trace
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, this.constructor)
        }
    }

    toJSON() {
        return {
            error: this.name,
            message: this.message,
            statusCode: this.statusCode,
            code: this.code,
            details: this.details,
            timestamp: this.timestamp,
        }
    }
}

// 400 - Bad Request
export class BadRequestError extends HttpError {
    constructor(message = 'Bad Request', code = 'BAD_REQUEST', details = null) {
        super(message, 400, code, details)
    }
}

// 401 - Unauthorized
export class UnauthorizedError extends HttpError {
    constructor(
        message = 'Unauthorized',
        code = 'UNAUTHORIZED',
        details = null
    ) {
        super(message, 401, code, details)
    }
}

// 403 - Forbidden
export class ForbiddenError extends HttpError {
    constructor(message = 'Forbidden', code = 'FORBIDDEN', details = null) {
        super(message, 403, code, details)
    }
}

// 404 - Not Found
export class NotFoundError extends HttpError {
    constructor(message = 'Not Found', code = 'NOT_FOUND', details = null) {
        super(message, 404, code, details)
    }
}

// 405 - Method Not Allowed
export class MethodNotAllowedError extends HttpError {
    constructor(
        message = 'Method Not Allowed',
        code = 'METHOD_NOT_ALLOWED',
        details = null
    ) {
        super(message, 405, code, details)
    }
}

// 408 - Request Timeout
export class RequestTimeoutError extends HttpError {
    constructor(
        message = 'Request Timeout',
        code = 'REQUEST_TIMEOUT',
        details = null
    ) {
        super(message, 408, code, details)
    }
}

// 409 - Conflict
export class ConflictError extends HttpError {
    constructor(message = 'Conflict', code = 'CONFLICT', details = null) {
        super(message, 409, code, details)
    }
}

// 422 - Unprocessable Entity
export class UnprocessableEntityError extends HttpError {
    constructor(
        message = 'Unprocessable Entity',
        code = 'UNPROCESSABLE_ENTITY',
        details = null
    ) {
        super(message, 422, code, details)
    }
}

// 429 - Too Many Requests
export class TooManyRequestsError extends HttpError {
    constructor(
        message = 'Too Many Requests',
        code = 'TOO_MANY_REQUESTS',
        details = null
    ) {
        super(message, 429, code, details)
    }
}

// 500 - Internal Server Error
export class InternalServerError extends HttpError {
    constructor(
        message = 'Internal Server Error',
        code = 'INTERNAL_SERVER_ERROR',
        details = null
    ) {
        super(message, 500, code, details)
    }
}

// 502 - Bad Gateway
export class BadGatewayError extends HttpError {
    constructor(message = 'Bad Gateway', code = 'BAD_GATEWAY', details = null) {
        super(message, 502, code, details)
    }
}

// 503 - Service Unavailable
export class ServiceUnavailableError extends HttpError {
    constructor(
        message = 'Service Unavailable',
        code = 'SERVICE_UNAVAILABLE',
        details = null
    ) {
        super(message, 503, code, details)
    }
}

// 504 - Gateway Timeout
export class GatewayTimeoutError extends HttpError {
    constructor(
        message = 'Gateway Timeout',
        code = 'GATEWAY_TIMEOUT',
        details = null
    ) {
        super(message, 504, code, details)
    }
}

// Domain-specific errors for your routing system

// When no route pattern matches the request path
export class RouteNotFoundError extends NotFoundError {
    constructor(path, groupId, availableRoutes = []) {
        super(
            `No route found for path '${path}' in group '${groupId}'`,
            'ROUTE_NOT_FOUND',
            {
                path,
                groupId,
                availableRoutes,
            }
        )
    }
}

// When the target node is not connected/available
export class NodeUnavailableError extends ServiceUnavailableError {
    constructor(instanceId, groupId) {
        super(
            `Node '${instanceId}' is not available in group '${groupId}'`,
            'NODE_UNAVAILABLE',
            {
                instanceId,
                groupId,
            }
        )
    }
}

// When a node doesn't respond within the timeout
export class NodeTimeoutError extends GatewayTimeoutError {
    constructor(instanceId, timeout = 30000) {
        super(
            `Node '${instanceId}' did not respond within ${timeout}ms`,
            'NODE_TIMEOUT',
            {
                instanceId,
                timeout,
            }
        )
    }
}

// When trying to register a route that already exists
export class RouteConflictError extends ConflictError {
    constructor(pattern, groupId, existingInstanceId, newInstanceId) {
        super(
            `Route pattern '${pattern}' is already registered by instance '${existingInstanceId}'`,
            'ROUTE_CONFLICT',
            {
                pattern,
                groupId,
                existingInstanceId,
                newInstanceId,
            }
        )
    }
}

// When a client tries to register an invalid route pattern
export class InvalidRoutePatternError extends BadRequestError {
    constructor(pattern, reason) {
        super(
            `Invalid route pattern '${pattern}': ${reason}`,
            'INVALID_ROUTE_PATTERN',
            {
                pattern,
                reason,
            }
        )
    }
}

// When a node fails to handle a request properly
export class NodeProcessingError extends BadGatewayError {
    constructor(instanceId, originalError) {
        super(
            `Node '${instanceId}' failed to process request: ${originalError.message}`,
            'NODE_PROCESSING_ERROR',
            {
                instanceId,
                originalError: originalError.message,
            }
        )
    }
}

// When a group doesn't exist
export class GroupNotFoundError extends NotFoundError {
    constructor(groupId) {
        super(`Group '${groupId}' does not exist`, 'GROUP_NOT_FOUND', {
            groupId,
        })
    }
}

// Utility function to check if an error is an HttpError
export function isHttpError(error) {
    return error instanceof HttpError
}

// Utility function to create error response object
export function createErrorResponse(error) {
    if (isHttpError(error)) {
        return {
            statusCode: error.statusCode,
            body: error.toJSON(),
        }
    }

    // Handle non-HTTP errors
    return {
        statusCode: 500,
        body: {
            error: 'InternalServerError',
            message: 'An unexpected error occurred',
            statusCode: 500,
            code: 'INTERNAL_SERVER_ERROR',
            details: error.message,
            timestamp: new Date().toISOString(),
        },
    }
}
